import { useState } from 'react';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Me } from '../../../../lib/me';
import { useBasePath } from '../../../../lib/use-base-path';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useUnreadNotifications } from '../../../../lib/use-unread-notifications';
import { useTranslation } from '../../../../lib/i18n';
import { Avatar, Button, IconButton, ProgressBar } from '../../ui';
import { EarnCreditsOffer } from '../../earn-credits/EarnCreditsOffer';
import { BuyCreditsModal } from '../../../settings/shared';
import { BellIcon } from './icons';
import { useAccountSummary } from '../hooks/use-account-summary';
import { useCreditsSummary } from '../hooks/use-credits-summary';
import { useCreditsWidgetState } from '../../../../lib/use-credits-widget-state';
import { CreditsMenuWidget } from '../../credits-widgets/CreditsMenuWidget';
import { usePersonalSaleActive } from '../../../../lib/use-personal-sale';

// The WIDE rail's account card: the credits card above an account row (avatar +
// name + the notifications bell). The avatar opens Settings — the web analog of
// mobile's Menu → Settings — and carries the hidden brand-toggle long press.
// Who it shows and what it computes lives in useAccountSummary / useCreditsSummary;
// the mobile drawer renders the same models with its own markup.
export function AccountCard({ me, collapsed = false }: { me: Me | null; collapsed?: boolean }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const leaveChat = useLeaveChatOnNavigate();
  const router = useRouter();
  const { initials, displayName, email, photoUrl, onPhotoError, settings } = useAccountSummary(me);
  const [hovered, setHovered] = useState(false);

  // Reserve the card's height before `me` resolves so the rail doesn't jump.
  if (!initials) {
    return <div style={{ minHeight: 36 }} />;
  }

  return (
    <div className="orion-account-outer" style={{ position: 'relative' }}>
      <div
        className="orion-account-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: 0,
          background: 'transparent',
          borderRadius: 'var(--sym-radius-container)',
        }}
      >
        {/* Credits card sits above the account row (Figma order) */}
        <span className="orion-account-credits orion-rail-label">
          <GenericEventsRouteProvider value="credits">
            <CreditsCard />
          </GenericEventsRouteProvider>
        </span>

        {/* Account row: in expanded → [avatar btn] [bell] horizontal.
            In collapsed → [bell] on top, [avatar btn] below (column-reverse),
            both 36px wide and left-aligned to sit within the icon-strip clip. */}
        <div style={{
          display: 'flex',
          flexDirection: collapsed ? 'column-reverse' : 'row',
          alignItems: collapsed ? 'flex-start' : 'center',
          gap: collapsed ? 16 : 4,
        }}>
          <button
            type="button"
            onClick={() => {
              settings.onClick();
              void router.push(settings.path);
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            aria-label={displayName || email}
            title={displayName || email}
            className="orion-account-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? 0 : 12,
              flex: collapsed ? undefined : 1,
              width: collapsed ? 36 : undefined,
              minWidth: 0,
              minHeight: 36,
              paddingBlock: 2,
              paddingInlineStart: collapsed ? 0 : 4,
              paddingInlineEnd: collapsed ? 0 : 8,
              justifyContent: collapsed ? 'center' : undefined,
              borderRadius: 8,
              border: 'none',
              background: hovered && !collapsed ? 'var(--ds-surface-standard-bg)' : 'transparent',
              cursor: 'pointer',
              textAlign: 'start',
              fontFamily: 'inherit',
              transition: 'background-color 0.15s ease',
            }}
          >
            {/* Circular avatar — the shared <Avatar>: photo, else initials over the gradient. */}
            <Avatar initials={initials} photoUrl={photoUrl} onPhotoError={onPhotoError} size={30} />
            <span className="orion-rail-label" style={{ minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 'var(--sym-weight-semibold)',
                  color: 'var(--ds-text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </span>
            </span>
          </button>
          {/* Bell icon — moved from the standalone NotificationsBell row into the account row */}
          <BellButton basePath={basePath} leaveChat={leaveChat} label={t('nav.notifications')} collapsed={collapsed} />
        </div>
      </div>
    </div>
  );
}

// Bell icon button — links to notifications, shows the unread dot if there are any.
function BellButton({
  basePath,
  leaveChat,
  label,
  collapsed,
}: {
  basePath: string;
  leaveChat: () => void;
  label: string;
  collapsed: boolean;
}) {
  const unreadCount = useUnreadNotifications();
  const router = useRouter();
  const linkProps = {
    href: '/notifications',
    'aria-label': label,
    'aria-current': router.pathname === '/notifications' ? ('page' as const) : undefined,
    onClick: () => {
      markNavFromDrawer();
      leaveChat();
    },
  };
  const dot = unreadCount > 0 && (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: collapsed ? 1 : 6,
        insetInlineEnd: collapsed ? 1 : 6,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: 'var(--sym-peach)',
        pointerEvents: 'none',
      }}
    />
  );
  if (collapsed) {
    return (
      <Link
        {...linkProps}
        as={`${basePath}/notifications`}
        title={label}
        className="orion-nav-item"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          padding: '6px 0',
          borderRadius: 'var(--ds-radius-8)',
          textDecoration: 'none',
        }}
      >
        <span
          aria-hidden
          className="orion-nav-icon"
          style={{ position: 'relative', width: 24, height: 24, flexShrink: 0, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ds-icon-primary)' }}
        >
          <BellIcon />
          {dot}
        </span>
      </Link>
    );
  }
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <IconButton {...linkProps} linkAs={`${basePath}/notifications`} size="medium">
        <BellIcon />
      </IconButton>
      {dot}
    </span>
  );
}

// Credits card — plan name + its CTA + credit count + progress bar.
// The card body does what the CTA does: the package picker, or Buy-more-credits on the top tier (see
// `CreditsSummary.upgrade`). Body and CTA must not nest — an <a> inside an <a> is invalid — so the
// body is a stretched hit target behind the content, and the content is click-through
// (`pointer-events: none`) except for the CTA. The hit target is out of the tab order and hidden from
// assistive tech: the CTA is the same action, already focusable and labelled. On the top-up arm it is
// a bare <span>, not a button element — a mouse-only surface, not a control (see `button-spec.test.ts`).
// Mirrors both phones' drawer card (iOS `DrawerOverlay.swift`, Android `Drawer.kt`).
export function CreditsCard() {
  const { t } = useTranslation();
  const credits = useCreditsSummary();
  // Personal sale: while the offer is live the credits CTA reads "Upgrade and save 30%" instead of the
  // ordinary label — it stays in the same full-width foot slot every arm now uses.
  const saleActive = usePersonalSaleActive();
  // The low/out-of-credits card REPLACES the ordinary credits card in this slot (Figma 16346-94984 /
  // -94588) whenever the balance is actually low/out (inside `useCreditsWidgetState`), so a comfortable
  // account is today's card.
  const widget = useCreditsWidgetState();
  const [buyOpen, setBuyOpen] = useState(false);
  if (widget.level !== 'none') return <CreditsMenuWidget />;
  if (!credits) return null;

  // The CTA is a full-width button at the FOOT of the card in every case, under the meter (Figma
  // 16989-171631). `fullWidth` `small` DS button; accent, not the black primary — Figma 15375-104637
  // binds it to `Fill (buttons)/Accent Brand/Default` over `Text & Icon/Primary light` (the brand lime).
  //
  // Two arms inside it, because the top tier sells a top-up rather than a plan (see the CTA note on
  // `CreditsSummary.upgrade`). The `packages` arm stays a real <a> so cmd-click still works; the
  // `topup` arm must NOT be one — it opens a modal, and an href would let a middle-click navigate to
  // the picker instead.
  // While the offer is live the CTA sells the discount ("Upgrade and save 30%"); otherwise it is the
  // ordinary Upgrade / Switch / Get more credits label. A personal-sale account is always Free, so this
  // is always the picker (packages) arm — but the label swap covers both arms for safety.
  const ctaLabel = saleActive ? t('personalSale.creditsCta') : credits.upgrade.label;
  const cta =
    credits.upgrade.kind === 'topup' ? (
      <Button
        color="accent"
        size="small"
        fullWidth
        onClick={() => {
          credits.upgrade.onClick();
          setBuyOpen(true);
        }}
      >
        {ctaLabel}
      </Button>
    ) : (
      <Button
        color="accent"
        size="small"
        fullWidth
        href={credits.upgrade.route}
        linkAs={credits.upgrade.path}
        onClick={credits.upgrade.onClick}
      >
        {ctaLabel}
      </Button>
    );

  return (
    <div style={{ position: 'relative', borderRadius: 16 }}>
      {credits.upgrade.kind === 'topup' ? (
        <span
          aria-hidden
          title={credits.title}
          onClick={() => {
            credits.upgrade.onClick();
            setBuyOpen(true);
          }}
          style={{ position: 'absolute', inset: 0, borderRadius: 16, zIndex: 0, cursor: 'pointer' }}
        />
      ) : (
        <Link
          href={credits.upgrade.route}
          as={credits.upgrade.path}
          onClick={credits.upgrade.onClick}
          tabIndex={-1}
          aria-hidden
          title={credits.title}
          style={{ position: 'absolute', inset: 0, borderRadius: 16, zIndex: 0 }}
        />
      )}
      <div
        // The card fill and the meter's track are the two theme-dependent values, so they come from
        // `.orion-credits-card` in globals.css — an inline style cannot carry the dark rule, and both
        // differ in dark (Surface/Soft #333232 under a #191919 track, per Monday 12880537585).
        className="orion-credits-card"
        style={{
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          padding: 16,
          borderRadius: 16,
          color: 'inherit',
        }}
      >
        {/* Head: the plan name alone (the CTA has moved to the full-width foot in every case). The
            earn-credits offer (red band only, behind its OWN gate) stays here — a secondary control that
            never became the primary.

            The CTA is offered on every plan: neither native card gates it on free/paid either, and both
            hide only the whole card (on `creditsCap <= 0`, our "no meter" case). `upgrade.label` is what
            says Upgrade / Switch / Get more credits. Parity on the PICKER arm is the GATE, not the
            destination — native picks between the Symphony picker and the Wixel page itself (behind a
            confirmation), where web always opens /packages and that page decides. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 16, fontWeight: 'var(--sym-weight-semibold)', letterSpacing: '-0.04em', lineHeight: '20px', color: 'var(--ds-text-primary)', whiteSpace: 'nowrap' }}>
            {credits.planLabel}
          </span>
          <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 8, pointerEvents: 'auto' }}>
            <EarnCreditsOffer entry={{ kind: 'sidebar_credits', fraction: credits.fraction }} pageName="menu" />
          </span>
        </div>
        {/* The credit count over its meter, kept in every case — the "{used} of {cap}" line is the
            ProgressBar's own label above the track. `.orion-sidebar` sets nowrap for the wide rail's
            collapse clip, so this sentence opts back out or it is clipped rather than wrapped — a full
            sentence carrying two numbers whose longest translations run ~35% past the English. */}
        <ProgressBar
          progress={credits.progress}
          value={credits.fraction * 100}
          label={credits.usageLabel}
          decorative
        />
        {/* The CTA: a full-width lime button at the foot, under the meter, in every case. */}
        <span style={{ pointerEvents: 'auto', display: 'block' }}>{cta}</span>
      </div>
      {credits.buyCredits && (
        <BuyCreditsModal
          open={buyOpen}
          onClose={() => setBuyOpen(false)}
          topup={credits.buyCredits.topup}
          planName={credits.buyCredits.planName}
          checkoutHref={credits.buyCredits.checkoutHref}
        />
      )}
    </div>
  );
}
